// Owned by task 2.4 of the dashboard-redesign spec (Requirement 8).
//
// Reads the tenant's bank accounts (owned by the `banking` spec) and
// exposes them as `WidgetResult<CashAtBankSummary>` for
// `Widget_CashAtBank` to consume. This hook is READ-ONLY: no mutation, no
// derived writes to the GL — Cash-at-Bank is a presentation view of the
// General Ledger (Constitution Rule 2), not a source of postings.
//
// Fetch contract (see design.md §Low-Level Design §Fetch contract):
//   - Starts in `{ status: "loading" }`.
//   - On mount and every `scope` change: an `AbortController`-guarded GET.
//   - Empty state when the accounts array is empty.
//   - Ready state carries a parsed `CashAtBankSummary`:
//       totalPence   = Σ balancePence across every account
//       trustPence   = Σ balancePence WHERE trust === true
//       operatingPence = Σ balancePence WHERE trust === false
//     By construction `trustPence + operatingPence === totalPence` for
//     ANY input — that invariant is what s42 LTA 1987 (client-money
//     segregation) is really asking us to prove.
//   - Error state carries an `errorId` (from the `X-Error-Id` response
//     header if present, else a locally-generated UUID v4) so the scoped
//     error card can render it verbatim for support (Requirement 14).
//   - `retry` re-invokes the fetch by bumping a retry counter.
//   - On unmount: aborts the in-flight fetch.
//
// The `api` client (frontend/src/services/api.ts) already attaches the
// Clerk JWT and the `x-clerk-subdomain` tenant header via interceptors —
// this hook MUST NOT re-implement any of that.
//
// Type-safety note: the shared `BankAccount` model in
// `@akfdev-com/shared` does NOT yet include `trust: boolean` or
// `balancePence: number` (see the `@todo` in `../types.ts`). The parser
// below therefore inspects the raw response body defensively rather than
// relying on the shared type to contain those fields.

import { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import api from "../../../services/api";
import type {
  BuildingScope,
  WidgetResult,
  CashAtBankSummary,
} from "../types";

/** User-safe message shown in the scoped error card. Never leaks server text. */
const USER_SAFE_ERROR_MESSAGE = "Could not load cash at bank";

/**
 * Generate a UUID v4. Prefers `globalThis.crypto.randomUUID` when available;
 * falls back to a small `Math.random`-based hex formatter for environments
 * that don't expose the WebCrypto API (older test runners, JSDOM shims,
 * etc.). Never used to authenticate anything — only to correlate a UI
 * error card to a log entry when the server didn't return an
 * `X-Error-Id` header.
 */
function generateLocalErrorId(): string {
  const cryptoRef = (globalThis as { crypto?: Crypto }).crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === "function") {
    return cryptoRef.randomUUID();
  }
  // RFC 4122 v4 shape: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx where
  // y ∈ {8, 9, a, b}.
  const rand = (): number => Math.floor(Math.random() * 16);
  const hex = (n: number): string => n.toString(16);
  let out = "";
  for (let i = 0; i < 36; i += 1) {
    if (i === 8 || i === 13 || i === 18 || i === 23) {
      out += "-";
    } else if (i === 14) {
      out += "4";
    } else if (i === 19) {
      out += hex((rand() & 0x3) | 0x8);
    } else {
      out += hex(rand());
    }
  }
  return out;
}

/**
 * Extract an `X-Error-Id` header from an axios error's response.
 * Case-insensitive per RFC 7230; different transports normalise headers
 * differently (Node http lowercases everything, JSDOM leaves them as-is).
 */
function extractErrorIdFromAxios(err: unknown): string | undefined {
  if (!err || typeof err !== "object") return undefined;
  const response = (err as AxiosError).response;
  if (!response || !response.headers) return undefined;
  const headers = response.headers as
    | Record<string, string | undefined>
    | { get?: (key: string) => string | null };
  // Some axios versions expose headers as an AxiosHeaders instance with a
  // `get(key)` method; others expose a plain record. Handle both defensively.
  if (typeof (headers as { get?: unknown }).get === "function") {
    const raw = (headers as { get: (key: string) => string | null }).get(
      "x-error-id",
    );
    if (typeof raw === "string" && raw.length > 0) return raw;
  }
  const record = headers as Record<string, string | undefined>;
  const candidate =
    record["x-error-id"] ??
    record["X-Error-Id"] ??
    record["X-ERROR-ID"];
  return typeof candidate === "string" && candidate.length > 0
    ? candidate
    : undefined;
}

/**
 * The narrowed account shape the widget actually needs. Kept in-file
 * rather than exported from `../types` because the widget itself will
 * only consume the aggregated `CashAtBankSummary["accounts"]`, which
 * already has this shape.
 */
type DashboardAccount = CashAtBankSummary["accounts"][number];

/**
 * Parse one raw account entry from the API response. Returns `undefined`
 * for any shape mismatch so the caller can reject the whole payload —
 * we never silently coerce or fill defaults for financial values.
 */
function parseAccount(raw: unknown): DashboardAccount | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const r = raw as Record<string, unknown>;
  const { id, buildingId, name, trust, balancePence } = r;
  if (typeof id !== "string" || id.length === 0) return undefined;
  if (typeof buildingId !== "string" || buildingId.length === 0) {
    return undefined;
  }
  if (typeof name !== "string") return undefined;
  if (typeof trust !== "boolean") return undefined;
  if (
    typeof balancePence !== "number" ||
    !Number.isFinite(balancePence) ||
    !Number.isInteger(balancePence)
  ) {
    return undefined;
  }
  return { id, buildingId, name, trust, balancePence };
}

/**
 * Parse and validate the raw response body. Accepts either a bare array
 * of accounts (per the design's stated `GET /banking/accounts` shape) or
 * a `{ accounts: [...] }` envelope for forward-compatibility with other
 * list endpoints in this repo. Returns `undefined` on ANY shape
 * mismatch — the caller then transitions to the error state.
 */
function parseBody(body: unknown): DashboardAccount[] | undefined {
  let list: unknown;
  if (Array.isArray(body)) {
    list = body;
  } else if (body && typeof body === "object") {
    list = (body as Record<string, unknown>).accounts;
    if (!Array.isArray(list)) return undefined;
  } else {
    return undefined;
  }
  const out: DashboardAccount[] = [];
  for (const raw of list as unknown[]) {
    const parsed = parseAccount(raw);
    if (!parsed) return undefined;
    out.push(parsed);
  }
  return out;
}

/**
 * Compute the trust / operating / total split from a parsed account list.
 * By construction, `trustPence + operatingPence === totalPence` for any
 * input array (including the empty list, where all three are 0).
 */
function summarise(accounts: DashboardAccount[]): CashAtBankSummary {
  let totalPence = 0;
  let trustPence = 0;
  let operatingPence = 0;
  for (const a of accounts) {
    totalPence += a.balancePence;
    if (a.trust) {
      trustPence += a.balancePence;
    } else {
      operatingPence += a.balancePence;
    }
  }
  return { totalPence, trustPence, operatingPence, accounts };
}

export function useCashAtBank(
  scope: BuildingScope,
): WidgetResult<CashAtBankSummary> {
  const [retryCounter, setRetryCounter] = useState(0);
  const [result, setResult] = useState<WidgetResult<CashAtBankSummary>>({
    status: "loading",
  });

  const retry = useCallback(() => {
    setRetryCounter((n) => n + 1);
  }, []);

  const scopeKind = scope.kind;
  const scopeBuildingId = scope.kind === "single" ? scope.buildingId : null;

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;

    // Reset to loading on scope/retry change so consumers see the
    // skeleton between fetches instead of stale ready data.
    setResult({ status: "loading" });

    const buildingId =
      scopeKind === "all" ? "ALL" : (scopeBuildingId ?? "ALL");
    const url = `/banking/accounts?buildingId=${encodeURIComponent(buildingId)}`;

    api
      .get(url, { signal: controller.signal })
      .then((response) => {
        if (cancelled) return;
        const accounts = parseBody(response?.data);
        if (!accounts) {
          setResult({
            status: "error",
            errorId: generateLocalErrorId(),
            message: "Unexpected response shape",
            retry,
          });
          return;
        }
        if (accounts.length === 0) {
          setResult({ status: "empty" });
          return;
        }
        setResult({ status: "ready", data: summarise(accounts) });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // The browser/axios raises `CanceledError` (or a `DOMException`
        // named `AbortError`) when the AbortController fires. That path
        // is the caller's intent, not a failure — swallow silently.
        if (controller.signal.aborted) return;
        const errorId =
          extractErrorIdFromAxios(err) ?? generateLocalErrorId();
        setResult({
          status: "error",
          errorId,
          message: USER_SAFE_ERROR_MESSAGE,
          retry,
        });
      });

    return () => {
      cancelled = true;
      controller.abort();
    };
    // `scope.kind` and `scope.buildingId` are the only inputs;
    // `retryCounter` triggers a re-fetch on `retry()`. `retry` and `api`
    // are stable refs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeKind, scopeBuildingId, retryCounter]);

  return result;
}
